import api from '../../../core/api/client';

// ─── Resource normalizer / serializer ────────────────────────────────────────

const normalizeResource = (r) => {
  if (!r || typeof r !== 'object') return r;

  const sc = r.status_counts ?? {};
  const meta = r.metadata ?? {};
  const statusLabel = meta.status ?? (typeof r.status === 'string' ? r.status : null);

  return {
    ...r,
    status: {
      available:   sc.available   ?? meta.available   ?? 0,
      inUse:       sc.in_use      ?? sc.inUse         ?? meta.inUse ?? 0,
      maintenance: sc.maintenance ?? meta.maintenance ?? 0,
    },
    statusLabel,
    availableQuantity: r.available_quantity ?? r.availableQuantity ?? 0,
    imagePaths: r.image_paths ?? r.imagePaths ?? [],
    userId:     r.user_id     ?? r.userId     ?? null,
    createdAt:  r.created_at  ?? r.createdAt  ?? null,
    updatedAt:  r.updated_at  ?? r.updatedAt  ?? null,
    company:      meta.company      ?? r.company      ?? null,
    serialNumber: meta.serialNumber ?? meta.serial_number ?? r.serialNumber ?? null,
    condition:    meta.condition    ?? r.condition    ?? null,
    licenseType:  meta.licenseType  ?? meta.license_type  ?? r.licenseType ?? null,
    expiryDate:   meta.expiryDate   ?? meta.expiry_date   ?? r.expiryDate  ?? null,
  };
};

const normalizeResourceList = (payload) => {
  const arr = Array.isArray(payload) ? payload : (payload?.data ?? payload?.results ?? []);
  return arr.map(normalizeResource);
};

const serializeResource = (data) => {
  if (!data || typeof data !== 'object') return {};
  const {
    imageFiles, imagesToDelete, userId, updatedAt, statusLabel,
    status, totalQuantity, availableQuantity,
    company, serialNumber, condition, licenseType, expiryDate,
    ...rest
  } = data;

  const payload = { ...rest };

  if (totalQuantity !== undefined) {
    payload.quantity = totalQuantity;
  } else if (status && typeof status === 'object') {
    payload.quantity = (status.available ?? 0) + (status.inUse ?? 0) + (status.maintenance ?? 0);
  }

  if (status?.available !== undefined) {
    payload.available_quantity = status.available;
  } else if (availableQuantity !== undefined) {
    payload.available_quantity = availableQuantity;
  }

  const metaExtras = {};
  if (typeof status === 'string')  metaExtras.status       = status;
  if (company     !== undefined)   metaExtras.company      = company;
  if (serialNumber!== undefined)   metaExtras.serialNumber = serialNumber;
  if (condition   !== undefined)   metaExtras.condition    = condition;
  if (licenseType !== undefined)   metaExtras.licenseType  = licenseType;
  if (expiryDate  !== undefined)   metaExtras.expiryDate   = expiryDate;

  if (Object.keys(metaExtras).length > 0) {
    payload.metadata = { ...(rest.metadata ?? {}), ...metaExtras };
  }

  return payload;
};

const serializeStatusCounts = (counts) => ({
  status_counts: {
    available:   counts.available   ?? counts.status?.available   ?? 0,
    in_use:      counts.in_use      ?? counts.inUse               ?? counts.status?.inUse ?? 0,
    maintenance: counts.maintenance ?? counts.status?.maintenance ?? 0,
  },
});

const normalizeResourceUpdateArgs = (arg1, arg2, arg3) => {
  if (typeof arg1 === 'string' && typeof arg2 === 'string' && arg3 !== undefined) {
    return { resourceId: arg2, updates: { ...arg3, type: arg1 } };
  }
  return { resourceId: arg1, updates: arg2 };
};

const resolveDeleteId = (arg1, arg2) => (arg2 !== undefined ? arg2 : arg1);

// ─── Booking normalizer / serializer ─────────────────────────────────────────

// Reconstruct the time-slot array from API start_time / end_time.
// E.g. start='09:00', end='11:00' → ['09:00-10:00', '10:00-11:00']
const generateTimeSlots = (startTime, endTime) => {
  if (!startTime || !endTime) return [];
  const [sh] = startTime.split(':').map(Number);
  const [eh] = endTime.split(':').map(Number);
  const slots = [];
  for (let h = sh; h < eh; h++) {
    const next = h + 1;
    slots.push(
      `${String(h).padStart(2, '0')}:00-${String(next).padStart(2, '0')}:00`
    );
  }
  return slots;
};

const normalizeBooking = (b) => {
  if (!b || typeof b !== 'object') return b;
  const startTime = b.start_time ?? b.startTime ?? null;
  const endTime   = b.end_time   ?? b.endTime   ?? null;
  const slots = generateTimeSlots(startTime, endTime);
  return {
    ...b,
    userId:     b.user_id     ?? b.userId     ?? null,
    resourceId: b.resource_id ?? b.resourceId ?? null,
    startTime,
    endTime,
    // Backward-compat arrays for calendar / booking modal components
    timeSlots:         b.timeSlots         ?? slots,
    affectedTimeSlots: b.affectedTimeSlots ?? slots,
    createdAt:  b.created_at  ?? b.createdAt  ?? null,
    updatedAt:  b.updated_at  ?? b.updatedAt  ?? null,
  };
};

// Component sends { date, venue, attendees, purpose, timeSlots, affectedTimeSlots }
// API needs   { resource_id, venue, title, purpose, date, start_time, end_time, status }
const serializeBooking = (data) => {
  if (!data || typeof data !== 'object') return {};
  const {
    timeSlots, affectedTimeSlots,
    attendees, userId, action, id,
    startTime, endTime,
    resourceId,
    ...rest
  } = data;

  const payload = { ...rest };

  // Map resource_id
  if (resourceId !== undefined) payload.resource_id = resourceId;

  // Derive start_time / end_time from slot array or explicit fields
  const slots = timeSlots ?? affectedTimeSlots ?? [];
  if (slots.length > 0) {
    const [firstStart] = slots[0].split('-');
    const [, lastEnd]  = slots[slots.length - 1].split('-');
    payload.start_time = firstStart;
    payload.end_time   = lastEnd;
  } else {
    if (startTime !== undefined) payload.start_time = startTime;
    if (endTime   !== undefined) payload.end_time   = endTime;
  }

  // Store attendees in metadata (not a top-level API field)
  if (attendees !== undefined) {
    payload.metadata = { ...(payload.metadata ?? {}), attendees };
  }

  if (!payload.status) payload.status = 'confirmed';

  return payload;
};

// ─── Borrowing normalizer ─────────────────────────────────────────────────────

const normalizeBorrowing = (b) => {
  if (!b || typeof b !== 'object') return b;
  return {
    ...b,
    hardwareId:         b.hardware_id          ?? b.hardwareId         ?? null,
    userId:             b.user_id              ?? b.userId             ?? null,
    expectedReturnDate: b.expected_return_date ?? b.expectedReturnDate ?? null,
    actualReturnDate:   b.actual_return_date   ?? b.actualReturnDate   ?? null,
    returnCondition:    b.return_condition     ?? b.returnCondition    ?? null,
    // Compat aliases for component field names
    startDate:  b.created_at          ?? b.startDate  ?? null,
    endDate:    b.expected_return_date ?? b.endDate    ?? null,
    returnDate: b.actual_return_date   ?? b.returnDate ?? null,
    quantity:   b.quantity             ?? 1,
    createdAt:  b.created_at           ?? b.createdAt  ?? null,
    updatedAt:  b.updated_at           ?? b.updatedAt  ?? null,
  };
};

const normalizeBorrowingList = (payload) => {
  const arr = Array.isArray(payload) ? payload : (payload?.data ?? []);
  return arr.map(normalizeBorrowing);
};

// ─── Service ─────────────────────────────────────────────────────────────────

export const resourceService = {
  // ── Resources ──────────────────────────────────────────────────────────────

  async getResources(type) {
    const response = await api.get('/resources', {
      params: type ? { type } : undefined,
    });
    return normalizeResourceList(response.data);
  },

  async createResource(resourceData) {
    const response = await api.post('/resources', serializeResource(resourceData));
    return normalizeResource(response.data);
  },

  async addResource(typeOrData, maybeData) {
    const data = maybeData ? { ...maybeData, type: typeOrData } : typeOrData;
    return this.createResource(data);
  },

  async getResourceById(resourceId) {
    const response = await api.get(`/resources/${resourceId}`);
    return normalizeResource(response.data);
  },

  async updateResource(arg1, arg2, arg3) {
    const { resourceId, updates } = normalizeResourceUpdateArgs(arg1, arg2, arg3);
    const response = await api.patch(`/resources/${resourceId}`, serializeResource(updates));
    return normalizeResource(response.data);
  },

  async deleteResource(arg1, arg2) {
    const resourceId = resolveDeleteId(arg1, arg2);
    const response = await api.delete(`/resources/${resourceId}`);
    return response.data;
  },

  async removeResource(resourceId) {
    return this.deleteResource(resourceId);
  },

  // PATCH /resources/{id}/status-counts  { status_counts: { available, in_use, maintenance } }
  async updateStatusCounts(resourceId, statusCounts) {
    const response = await api.patch(
      `/resources/${resourceId}/status-counts`,
      serializeStatusCounts(statusCounts)
    );
    return normalizeResource(response.data);
  },

  // ── Bookings ───────────────────────────────────────────────────────────────

  // GET /bookings  (server-side auth determines visibility)
  async getBookings(startDate, endDate) {
    if (startDate && endDate) {
      return this.getBookingsInRange(startDate, endDate);
    }
    const response = await api.get('/bookings');
    return response.data.map(normalizeBooking);
  },

  // GET /bookings/in-range?start=...&end=...
  async getBookingsInRange(startDate, endDate) {
    const toISO = (d) => (d instanceof Date ? d.toISOString() : d);
    const response = await api.get('/bookings/in-range', {
      params: { start: toISO(startDate), end: toISO(endDate) },
    });
    return response.data.map(normalizeBooking);
  },

  // GET /bookings/resources/{resource_id}
  async getResourceBookings(resourceId) {
    const response = await api.get(`/bookings/resources/${resourceId}`);
    return response.data.map(normalizeBooking);
  },

  // GET /bookings/{id}
  async getBookingById(bookingId) {
    const response = await api.get(`/bookings/${bookingId}`);
    return normalizeBooking(response.data);
  },

  // POST /bookings  { resource_id, venue, title, purpose, date, start_time, end_time, status }
  async createBooking(bookingData) {
    const response = await api.post('/bookings', serializeBooking(bookingData));
    return normalizeBooking(response.data);
  },

  async addBooking(bookingData) {
    return this.createBooking(bookingData);
  },

  // PATCH /bookings/{id}
  async updateBooking(bookingId, updates) {
    const response = await api.patch(`/bookings/${bookingId}`, serializeBooking(updates));
    return normalizeBooking(response.data);
  },

  // DELETE /bookings/{id}
  async deleteBooking(bookingId) {
    const response = await api.delete(`/bookings/${bookingId}`);
    return response.data;
  },

  async removeBooking(bookingId) {
    return this.deleteBooking(bookingId);
  },

  // ── Borrowings ─────────────────────────────────────────────────────────────

  // GET /borrowings  (server returns caller's records; admins see all)
  async getAllBorrowings() {
    const response = await api.get('/borrowings');
    return normalizeBorrowingList(response.data);
  },

  async getBorrowings() {
    return this.getAllBorrowings();
  },

  // GET /borrowings/{id}
  async getBorrowingById(borrowingId) {
    const response = await api.get(`/borrowings/${borrowingId}`);
    return normalizeBorrowing(response.data);
  },

  // GET /borrowings/hardware/{hardware_id}
  async getHardwareBorrowings(hardwareId) {
    const response = await api.get(`/borrowings/hardware/${hardwareId}`);
    return normalizeBorrowingList(response.data);
  },

  // Borrowings are created via POST /hardware/{id}/checkout — delegate there.
  // Accepts { hardwareId, endDate, purpose } (component shape).
  async createBorrowing(borrowingData) {
    const hardwareId = borrowingData.hardwareId ?? borrowingData.hardware_id;
    if (!hardwareId) throw new Error('hardwareId is required for borrowing');
    const response = await api.post(`/hardware/${hardwareId}/checkout`, {
      expected_return_date: borrowingData.endDate ?? borrowingData.expected_return_date,
      purpose: borrowingData.purpose,
    });
    // API returns the hardware item; synthesize a borrowing-shaped object for the component
    return normalizeBorrowing({
      hardware_id: hardwareId,
      status: 'active',
      expected_return_date: borrowingData.endDate ?? borrowingData.expected_return_date,
      purpose: borrowingData.purpose,
      created_at: new Date().toISOString(),
    });
  },

  // For admin status changes (approve/reject). The API uses checkout/checkin lifecycle;
  // this is a best-effort PATCH kept for the admin ResourceFlowSection.
  async updateBorrowing(borrowingId, updates) {
    const response = await api.patch(`/borrowings/${borrowingId}`, updates);
    return normalizeBorrowing(response.data);
  },
};
